// Editor visuale del template etichetta con preview live.
// L'utente regola posizione/dimensione/font dei singoli elementi e vede
// l'aggiornamento in tempo reale su un canvas SVG. Le modifiche sono
// persistite in localStorage (`trackeat:label_template:<id>`); LabelsPage
// le carica all'avvio se presenti, altrimenti usa il DEFAULT_TEMPLATE.

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  arrowDownOutline,
  arrowUpOutline,
  checkmarkOutline,
  closeOutline,
  globeOutline,
  shareOutline,
  trashOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Share } from '../lib/adapters/share/index.js';
import type { LabelElement } from '@trackeat/shared';
import { rectangularQr } from '../lib/adapters/printers/rectangularQr.js';
import { resolveBinding } from '../lib/adapters/printers/renderer.js';
import { recommendedStandardQrWidthMm, type RasterOptions } from '../lib/adapters/printers/labelRaster.js';
import { TsplLabelPreview } from './TsplLabelPreview.js';
import './LabelEditorModal.css';
import { confirmDialog } from '../lib/ui/dialogs.js';

// Forma minima del template usata dall'editor — coerente con la
// definizione locale in LabelsPage. Evita di richiedere tenant_id/
// created_at che non sono noti per i template hard-coded.
export interface EditableLabelTemplate {
  id: string;
  name: string;
  preset: string;
  width_mm: number;
  height_mm: number;
  elements: unknown[];
  business_id: string | null;
  required_allergens?: boolean;
}

export interface LabelEditorModalProps {
  isOpen: boolean;
  template: EditableLabelTemplate;
  /** Dati di esempio per la preview (stesso shape del job di stampa). */
  previewData: Record<string, unknown>;
  rasterOptions?: RasterOptions;
  /** Tenant attivo: serve a `clearCustomTemplate` per cancellare la chiave
   *  scoped, allineata a quella scritta da `saveCustomTemplate`. */
  tenantId?: string | null;
  onDismiss: () => void;
  onReset: () => void;
  /** Chiamato al Salva con il template aggiornato. */
  onSaved: (updated: EditableLabelTemplate) => void;
  /** True solo per il platform owner: abilita "Salva come default globale". */
  canSaveGlobal?: boolean;
  /** Salva il layout come default GLOBALE per il suo formato (owner). */
  onSaveGlobal?: (format: string, template: EditableLabelTemplate) => void | Promise<void>;
}

const STORAGE_KEY_PREFIX = 'trackeat:label_template:';

// Chiave scoped per tenant: evita collisioni quando l'account consulente
// gestisce più ristoranti (tenant_id diversi) dallo stesso dispositivo.
function templateStorageKey(id: string, tenantId?: string | null): string {
  return STORAGE_KEY_PREFIX + (tenantId ? `${tenantId}:` : '') + id;
}

export function loadCustomTemplate(id: string, tenantId?: string | null): EditableLabelTemplate | null {
  try {
    const raw = localStorage.getItem(templateStorageKey(id, tenantId));
    if (!raw) return null;
    return JSON.parse(raw) as EditableLabelTemplate;
  } catch {
    return null;
  }
}

export function saveCustomTemplate(t: EditableLabelTemplate, tenantId?: string | null): void {
  try {
    localStorage.setItem(templateStorageKey(t.id, tenantId), JSON.stringify(t));
  } catch {
    throw new Error('Layout non salvato: lo spazio sul dispositivo non è disponibile. Riprova senza chiudere l’editor.');
  }
}

export function clearCustomTemplate(id: string, tenantId?: string | null): void {
  try {
    localStorage.removeItem(templateStorageKey(id, tenantId));
  } catch {
    throw new Error('Impossibile ripristinare il layout sul dispositivo');
  }
}

const ELEMENT_TYPE_LABEL: Record<string, string> = {
  text: 'Testo',
  qr: 'QR code',
  barcode: 'Barcode',
  rect: 'Rettangolo',
  line: 'Linea',
  expiry_weekday: 'Giorno di scadenza',
};

const DEFAULT_BINDINGS: Array<{ label: string; binding: string }> = [
  { label: 'Nome prodotto',         binding: '{{ product | upper }}' },
  { label: 'Data produzione',       binding: 'Prod. {{ prepared_at | date:DD/MM/YYYY }}' },
  { label: 'Data scadenza',         binding: 'Scad. {{ expires_at | date:DD/MM/YYYY }}' },
  { label: 'Lotto',                 binding: 'Lotto: {{ lp_code | strip:LP- }}' },
  { label: 'Ingredienti',           binding: '{{ ingredients | ingredients }}' },
  { label: 'Allergeni',             binding: '{{ allergens | allergens }}' },
  { label: 'URL tracciabilità (QR)', binding: '{{ trace_url }}' },
];

export function LabelEditorModal(props: LabelEditorModalProps): JSX.Element {
  const { isOpen, template, previewData, onDismiss, onReset, onSaved, canSaveGlobal, onSaveGlobal } = props;
  const [working, setWorking] = useState<EditableLabelTemplate>(template);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const initializedSession = useRef<string | null>(null);
  useEffect(() => {
    const session = isOpen ? JSON.stringify([props.tenantId ?? null, template.id]) : null;
    // Query refreshes can recreate the same saved template object. The open
    // editor owns its draft until dismissal or an actual template/tenant change.
    if (initializedSession.current === session) return;
    initializedSession.current = session;
    if (isOpen) {
      setWorking(JSON.parse(JSON.stringify(template)) as EditableLabelTemplate);
      setExpandedId(null); setSaveError(null); setDragEnabled(false);
    }
  }, [isOpen, template, props.tenantId]);

  // Schermo largo (desktop / tablet landscape) → modale più ampio e alto.
  const [isWide, setIsWide] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (): void => setIsWide(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // L’inspector scorre indipendentemente dalla superficie di anteprima.
  const fieldsRef = useRef<HTMLDivElement | null>(null);
  const [dragEnabled, setDragEnabled] = useState(false);


  // Anteprima collassabile: su mobile la tastiera mangia metà schermo, quindi
  // poterla nascondere dà spazio ai campi numerici. Su mobile NON è sticky
  // (scorre via quando si scrolla ai campi); su desktop resta in vista.
  const [previewIssues, setPreviewIssues] = useState<string[]>([]);
  const [sampleProduct, setSampleProduct] = useState<string | null>(null);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);

  // In editor forziamo dati di esempio popolati così l'utente vede TUTTI
  // gli elementi (ingredienti/allergeni vuoti verrebbero scartati dal
  // renderer; il QR senza trace_url non si renderizzerebbe).
  // I valori reali del job di stampa restano quelli passati via prop.
  const editorPreviewData: Record<string, unknown> = useMemo(() => ({
    ...previewData,
    product: sampleProduct ?? previewData.product ?? 'Salsa di pomodoro',
    prepared_at: previewData.prepared_at || new Date().toISOString(),
    expires_at: previewData.expires_at || new Date(Date.now() + 3 * 86_400_000).toISOString(),
    lp_code: previewData.lp_code || 'LP-XXXXXXXX',
    ingredients: (Array.isArray(previewData.ingredients) && previewData.ingredients.length > 0)
      ? previewData.ingredients
      : ['pollo', 'riso', 'carote'],
    allergens: (Array.isArray(previewData.allergens) && previewData.allergens.length > 0)
      ? previewData.allergens
      : ['pesce', 'glutine'],
    trace_url: previewData.trace_url || 'https://trackeat.eu/t/abcdefabcdefabcdefabcdefabcdefab',
  }), [previewData, sampleProduct]);

  // Riquadro (in mm) di un elemento, per l'overlay di selezione/drag sopra
  // l'anteprima fedele. Per il testo senza larghezza esplicita stimiamo un
  // box afferrabile; il drag aggiorna comunque solo x/y.
  const elBox = useCallback((el: LabelElement): { x: number; y: number; w: number; h: number } => {
    const x = el.x_mm;
    const y = el.y_mm;
    if (el.type === 'qr' || el.type === 'expiry_weekday') {
      const s = el.width_mm ?? 18;
      return { x, y, w: s, h: el.type === 'qr' && el.qr_format === 'rmqr' ? el.height_mm ?? 9 : s };
    }
    if (el.type === 'barcode') {
      return { x, y, w: el.width_mm ?? 30, h: el.height_mm ?? 8 };
    }
    if (el.type === 'line' || el.type === 'rect') {
      return { x, y, w: el.width_mm ?? working.width_mm - x, h: Math.max(0.6, el.height_mm ?? 0.4) };
    }
    // text
    const pt = el.font_size_pt ?? 6;
    const lineH = Math.max(2.5, pt * 0.42);
    if (el.width_mm && el.width_mm > 0) {
      return { x, y, w: el.width_mm, h: Math.max(lineH, el.height_mm ?? lineH) };
    }
    const txt = el.binding ?? el.static_value ?? 'testo';
    const estW = Math.min(working.width_mm - x, Math.max(10, txt.length * pt * 0.16));
    return { x, y, w: estW, h: lineH };
  }, [working.width_mm]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ id: string; startX: number; startY: number; elX: number; elY: number } | null>(null);

  const onElementPointerDown = useCallback((e: React.PointerEvent, el: LabelElement): void => {
    e.preventDefault();
    e.stopPropagation();
    selectSettings(el.id);
    if (!dragEnabled) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      id: el.id, startX: e.clientX, startY: e.clientY,
      elX: el.x_mm, elY: el.y_mm,
    };
  }, [dragEnabled]);

  const onOverlayPointerMove = useCallback((e: React.PointerEvent): void => {
    const d = dragRef.current;
    const cont = containerRef.current;
    if (!d || !cont) return;
    const rect = cont.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const dxMm = ((e.clientX - d.startX) / rect.width) * working.width_mm;
    const dyMm = ((e.clientY - d.startY) / rect.height) * working.height_mm;
    const snap = (v: number): number => Math.round(v * 2) / 2; // 0.5 mm
    const element = (working.elements as LabelElement[]).find(el => el.id === d.id);
    if (!element) return;
    const bounds = elBox(element);
    const nx = Math.max(0, Math.min(working.width_mm - bounds.w, snap(d.elX + dxMm)));
    const ny = Math.max(0, Math.min(working.height_mm - bounds.h, snap(d.elY + dyMm)));
    patchElement(d.id, { x_mm: nx, y_mm: ny });
  }, [working.width_mm, working.height_mm, working.elements, elBox]);

  const endDrag = useCallback((): void => { dragRef.current = null; }, []);

  function patchElement(id: string, patch: Partial<LabelElement>): void {
    setWorking((w) => ({
      ...w,
      elements: w.elements.map((e) => {
        const el = e as LabelElement;
        return el.id === id ? { ...el, ...patch } : el;
      }),
    }));
  }

  function deleteElement(id: string): void {
    setWorking((w) => ({ ...w, elements: w.elements.filter((e) => (e as LabelElement).id !== id) }));
  }

  function moveElement(id: string, direction: -1 | 1): void {
    setWorking((w) => {
      const idx = w.elements.findIndex((e) => (e as LabelElement).id === id);
      if (idx === -1) return w;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= w.elements.length) return w;
      const next = [...w.elements];
      const [el] = next.splice(idx, 1);
      next.splice(newIdx, 0, el!);
      return { ...w, elements: next };
    });
  }

  function selectSettings(id: string): void {
    setExpandedId(id);
    if (fieldsRef.current) fieldsRef.current.scrollTop = 0;
  }

  function addElement(type: 'text' | 'qr' | 'expiry_weekday'): void {
    const id = `el-${Date.now().toString(36)}`;
    const newEl: LabelElement = type === 'expiry_weekday'
      ? { id, type, x_mm: 1, y_mm: 1, width_mm: 12, height_mm: 12, rotation: 0 }
      : type === 'qr'
      ? { id, type: 'qr', x_mm: 1, y_mm: 1, width_mm: 18, height_mm: 18, binding: '{{ trace_url }}', rotation: 0 }
      : { id, type: 'text', x_mm: 2, y_mm: 2, font_size_pt: 6, font_weight: 'normal', binding: 'Nuovo testo', rotation: 0 };
    if (type === 'expiry_weekday') {
      // Prefer free space without rearranging the customer's existing fields.
      const size = Math.min(12, working.width_mm, working.height_mm);
      newEl.width_mm = size;
      newEl.height_mm = size;
      const boxes = (working.elements as LabelElement[]).map(elBox);
      search: for (let y = 1; y <= working.height_mm - size; y += 0.5) {
        for (let x = 1; x <= working.width_mm - size; x += 0.5) {
          if (boxes.every(b => x + size <= b.x || x >= b.x + b.w || y + size <= b.y || y >= b.y + b.h)) {
            newEl.x_mm = x;
            newEl.y_mm = y;
            break search;
          }
        }
      }
    }
    setWorking((w) => ({ ...w, elements: [...w.elements, newEl] }));
    selectSettings(id);
  }

  // Dimensioni etichetta valide? Svuotare il campo larghezza/altezza porta il
  // valore a 0 → l'anteprima rasterizzerebbe un canvas 0×0 (getImageData lancia)
  // e l'overlay dividerebbe per zero → render throw → schermo bianco. Gate la
  // preview e il salvataggio su questo flag invece di crashare.
  const dimsValid = Number.isFinite(working.width_mm) && Number.isFinite(working.height_mm)
    && working.width_mm >= 10 && working.width_mm <= 200 && working.height_mm >= 10 && working.height_mm <= 200;

  function commit(): void {
    if (!dimsValid) return;
    try { onSaved(working); setSaveError(null); } catch (e) { setSaveError(e instanceof Error ? e.message : 'Layout non salvato'); }
  }

  const selectedEl = (working.elements as LabelElement[]).find((e) => e.id === expandedId) ?? null;

  const recommendedQrWidth = useMemo(() => {
    if (selectedEl?.type !== 'qr' || selectedEl.qr_format === 'rmqr') return null;
    try {
      const value = selectedEl.binding ? resolveBinding(selectedEl.binding, editorPreviewData) : selectedEl.static_value ?? '';
      return Math.ceil(recommendedStandardQrWidthMm(value, props.rasterOptions));
    } catch { return null; } // The preview reports invalid QR data.
  }, [selectedEl, editorPreviewData, props.rasterOptions]);

  const weekdayOverlaps = (selectedEl?.type === 'expiry_weekday' || (selectedEl?.type === 'qr' && selectedEl.qr_format === 'rmqr')) && (working.elements as LabelElement[]).some(other => {
    if (other.id === selectedEl.id) return false;
    const a = elBox(selectedEl);
    const b = elBox(other);
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
  });

  return (
    <IonModal
      data-testid="label-editor"
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      style={isWide
        ? ({ '--width': 'min(1100px, 96vw)', '--height': 'min(860px, 94vh)', '--border-radius': '16px' } as React.CSSProperties)
        : undefined}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss}>Annulla</IonButton>
          </IonButtons>
          <IonTitle>Etichetta</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => {
                const json = JSON.stringify(working);
                void Share.share({
                  title: 'trackEAT layout etichetta',
                  text: json,
                  dialogTitle: 'Esporta layout etichetta',
                }).catch(() => {
                  // Fallback: nessun foglio di condivisione → log copiabile.
                  // eslint-disable-next-line no-console
                  console.log('LAYOUT_JSON', json);
                });
              }}
              aria-label="Esporta layout"
            >
              <IonIcon icon={shareOutline} slot={isWide ? "start" : "icon-only"} />
              {isWide && "Esporta"}
            </IonButton>
            <IonButton color="primary" strong onClick={commit} disabled={!dimsValid}>
              <IonIcon icon={checkmarkOutline} slot="start" />
              Salva
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={false} style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div className="label-editor-workspace">
        {saveError && <p role="alert" style={{ padding: 16 }}>{saveError}</p>}
        <div className="label-editor-layout">
        {/* PREVIEW LIVE FEDELE (rasterizza il TSPL reale) + overlay drag */}
        <div className="label-editor-preview" style={{
          padding: previewCollapsed ? '8px 12px' : '12px 12px 10px',
          background: '#0f172a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}>
          <button
            type="button"
            onClick={() => setPreviewCollapsed((c) => !c)}
            style={{
              background: 'rgba(148,163,184,.18)', color: '#e2e8f0', border: 'none',
              borderRadius: 999, padding: '4px 14px', fontSize: 12, cursor: 'pointer',
            }}
          >
            {previewCollapsed ? '▸ Mostra anteprima' : '▾ Nascondi anteprima'}
          </button>
          <button type="button" className="label-editor-drag-toggle" aria-pressed={dragEnabled} onClick={() => { endDrag(); setDragEnabled(v => !v); }}>
            {dragEnabled ? 'Trascinamento attivo' : 'Attiva trascinamento'}
          </button>
          {!previewCollapsed && (
          <>
          {dimsValid ? (
          <div
            ref={containerRef}
            style={{ position: 'relative', width: '100%', maxWidth: isWide ? 440 : 240, touchAction: 'none' }}
          >
            <TsplLabelPreview template={working} data={editorPreviewData} rasterOptions={props.rasterOptions} onIssues={setPreviewIssues} />
            <div
              onPointerMove={onOverlayPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerLeave={endDrag}
              onPointerDown={() => setExpandedId(null)}
              style={{ position: 'absolute', inset: 0 }}
            >
              {(working.elements as LabelElement[]).map((el) => {
                const b = elBox(el);
                const sel = el.id === expandedId;
                return (
                  <button
                    type="button"
                    aria-label={`Seleziona ${ELEMENT_TYPE_LABEL[el.type] ?? el.type}: ${el.binding ?? el.static_value ?? el.id}`}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectSettings(el.id); } }}
                    key={el.id}
                    onPointerDown={(e) => onElementPointerDown(e, el)}
                    title={ELEMENT_TYPE_LABEL[el.type] ?? el.type}
                    style={{
                      position: 'absolute',
                      left: `${(b.x / working.width_mm) * 100}%`,
                      top: `${(b.y / working.height_mm) * 100}%`,
                      width: `${(b.w / working.width_mm) * 100}%`,
                      height: `${(b.h / working.height_mm) * 100}%`,
                      minWidth: 24, minHeight: 24,
                      boxSizing: 'border-box',
                      border: sel ? '2px solid #38bdf8' : '1px dashed rgba(148,163,184,.7)',
                      background: sel ? 'rgba(56,189,248,.18)' : 'transparent',
                      borderRadius: 3,
                      padding: 0, cursor: dragEnabled ? 'move' : 'pointer',
                      touchAction: 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
          ) : (
            <div style={{
              color: '#fbbf24', textAlign: 'center', maxWidth: 460,
              padding: '48px 20px', fontSize: 14, lineHeight: 1.5,
            }}>
              ⚠️ Imposta una <b>larghezza</b> e un&apos;<b>altezza</b> validi
              (da 10 a 200&nbsp;mm) per vedere l&apos;anteprima dell&apos;etichetta.
            </div>
          )}
          <div style={{ fontSize: 12, color: '#cbd5e1', textAlign: 'center', maxWidth: 460 }}>
            {selectedEl
              ? `${ELEMENT_TYPE_LABEL[selectedEl.type] ?? selectedEl.type} selezionato · modifica nel pannello`
              : 'Tocca un campo per modificarlo. Usa le frecce per spostarlo.'}
          </div>
          </>
          )}
        </div>

        <div className="label-editor-fields" ref={fieldsRef}>
          {previewIssues.length > 0 && <div role="alert" style={{ padding: 12, background: '#fee2e2', color: '#7f1d1d' }}>
            {previewIssues.map(message => <p key={message}>{message}</p>)}
          </div>}
        {!selectedEl && <>
        <section className="label-editor-add" aria-label="Aggiungi campi etichetta">
          <button type="button" className="label-editor-weekday" onClick={() => {
            const existing = (working.elements as LabelElement[]).find(el => el.type === 'expiry_weekday');
            if (existing) selectSettings(existing.id); else addElement('expiry_weekday');
          }}>
            <span className="label-editor-day-example" aria-hidden="true"><strong>M</strong><small>ar</small></span>
            <span><strong>Giorno di scadenza in evidenza</strong><small>{working.elements.some(el => (el as LabelElement).type === 'expiry_weekday') ? 'Modifica il riquadro presente' : 'Aggiungi il riquadro nero · giorno automatico'}</small></span>
          </button>
          <div className="label-editor-add-other">
            <IonButton fill="outline" onClick={() => addElement('text')}>Aggiungi testo</IonButton>
            <IonButton fill="outline" onClick={() => addElement('qr')}>Aggiungi QR</IonButton>
          </div>
        </section>
        <div style={{ padding: '12px 16px 24px', width: '100%', boxSizing: 'border-box' }}>
          {/* Dimensioni etichetta */}
          <h3 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            📏 Dimensioni etichetta
          </h3>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '12px 14px',
            boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
            display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <NumField
              label="Larghezza (mm)"
              value={working.width_mm}
              onChange={(v) => setWorking((w) => ({ ...w, width_mm: v }))}
              min={10} max={200} step={1}
            />
            <span style={{ color: '#94a3b8' }}>×</span>
            <NumField
              label="Altezza (mm)"
              value={working.height_mm}
              onChange={(v) => setWorking((w) => ({ ...w, height_mm: v }))}
              min={10} max={200} step={1}
            />
          </div>

          {/* Elenco elementi */}
          <h3 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            🔧 Elementi ({working.elements.length})
          </h3>
          {(working.elements as LabelElement[]).map((el) => {
            const selected = expandedId === el.id;
            const previewText = (el.type === 'text' && (el.binding || el.static_value))
              ? (el.binding ?? el.static_value ?? '')
              : ELEMENT_TYPE_LABEL[el.type] ?? el.type;
            return (
              <div
                key={el.id}
                role="button" tabIndex={0} aria-pressed={selected}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectSettings(el.id); } }}
                onClick={() => selectSettings(el.id)}
                style={{
                  background: selected ? '#eff6ff' : '#fff',
                  borderRadius: 14,
                  marginBottom: 8,
                  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  border: selected ? '2px solid #0066cc' : '2px solid transparent',
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: '#eff6ff', color: '#0066cc',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}>
                    {el.type === 'qr' ? '🔲' : el.type === 'barcode' ? '|||' : 'Aa'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                      {ELEMENT_TYPE_LABEL[el.type] ?? el.type}
                    </div>
                    <div style={{
                      fontSize: 11, color: '#64748b',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {previewText}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', flexShrink: 0 }}>
                    {el.x_mm.toFixed(0)},{el.y_mm.toFixed(0)} mm
                    {el.type === 'text' && el.font_size_pt ? ` · ${el.font_size_pt}pt` : ''}
                    {(el.type === 'qr' || el.type === 'barcode' || el.type === 'expiry_weekday') && el.width_mm ? ` · ${el.width_mm}mm` : ''}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Reset */}
          <IonButton
            expand="block"
            fill="clear"
            color="danger"
            onClick={async () => {
              if (await confirmDialog('Ripristinare il layout di default?', { okText: 'Ripristina', danger: true })) {
                try { onReset(); onDismiss(); } catch (e) { setSaveError(e instanceof Error ? e.message : 'Ripristino non riuscito'); }
              }
            }}
            style={{ marginTop: 16, '--border-radius': '10px' } as React.CSSProperties}
          >
            <IonIcon icon={closeOutline} slot="start" />
            Ripristina layout di default
          </IonButton>
          {/* Default globale (solo platform owner): salva questo layout come
              default del formato per TUTTI gli account (salvo varianti per
              ristorante). Pulsante separato dal Salva (che resta per-tenant). */}
          {canSaveGlobal && onSaveGlobal && (
            <details style={{ marginTop: 14 }}><summary style={{ padding: 12 }}>Opzioni amministratore · default per tutti gli account</summary>
              <IonButton
                expand="block"
                fill="outline"
                disabled={!dimsValid || savingGlobal}
                onClick={async () => {
                  const fmt = `${Math.round(working.width_mm)}x${Math.round(working.height_mm)}`;
                  // Double-check: è un'azione CROSS-TENANT (cambia il default di
                  // questo formato per TUTTI gli account). Un tap accidentale qui
                  // ha già causato un incidente — la conferma esplicita è voluta.
                  const confirmed = await confirmDialog(
                    `Stai per impostare questo layout come DEFAULT GLOBALE del formato ${fmt} ` +
                      `per ogni account TrackEAT. Sovrascrive il default globale attuale di questo formato. ` +
                      `(Le etichette personalizzate dei singoli ristoranti restano e hanno la precedenza.)`,
                    { header: '⚠️ Azione su TUTTI gli account', okText: 'Conferma', danger: true },
                  );
                  if (!confirmed) return;
                  setSavingGlobal(true);
                  void Promise.resolve(onSaveGlobal(fmt, working)).finally(() => setSavingGlobal(false));
                }}
                style={{ '--border-radius': '12px', '--border-color': '#0066cc', '--color': '#0066cc' } as React.CSSProperties}
              >
                <IonIcon icon={globeOutline} slot="start" />
                {savingGlobal
                  ? 'Salvataggio…'
                  : `Salva come default globale (${Math.round(working.width_mm)}×${Math.round(working.height_mm)})`}
              </IonButton>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, textAlign: 'center' }}>
                Diventa il default di questo formato per tutti gli account (le varianti per ristorante hanno la precedenza).
              </div>
            </details>
          )}

        </div>

        </>}
        {/* Il pannello sostituisce il catalogo senza spostare l’anteprima. */}
        {selectedEl && (() => {
          const el = selectedEl;
          const selIdx = (working.elements as LabelElement[]).findIndex((e) => e.id === el.id);
          return (
            <div key={selectedEl.id} className="label-editor-selected" style={{
              background: '#fff',
              border: '2px solid #38bdf8',
              borderRadius: 14,
              boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 6px 16px rgba(15,23,42,.08)',
              padding: '14px 16px 16px',
              margin: '12px auto',
              maxWidth: 640,
              width: 'calc(100% - 32px)',
              boxSizing: 'border-box',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  ⚙️ Impostazioni · {ELEMENT_TYPE_LABEL[el.type] ?? el.type}
                </div>
                <IonButton fill="clear" onClick={() => { setExpandedId(null); setDragEnabled(false); if (fieldsRef.current) fieldsRef.current.scrollTop = 0; }} aria-label="Tutti i campi">
                  Tutti i campi
                </IonButton>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <NumField label="Posizione X (mm)" value={el.x_mm} onChange={v => patchElement(el.id, { x_mm: v })} min={0} max={working.width_mm} />
                <NumField label="Posizione Y (mm)" value={el.y_mm} onChange={v => patchElement(el.id, { y_mm: v })} min={0} max={working.height_mm} />
              </div>
              <div className="label-editor-nudge" role="group" aria-label="Sposta di mezzo millimetro">
                {([{ label: 'Sinistra', dx: -0.5, dy: 0 }, { label: 'Destra', dx: 0.5, dy: 0 }, { label: 'Su', dx: 0, dy: -0.5 }, { label: 'Giù', dx: 0, dy: 0.5 }]).map(move => (
                  <button key={move.label} type="button" onClick={() => {
                    const bounds = elBox(el);
                    patchElement(el.id, {
                      x_mm: Math.max(0, Math.min(working.width_mm - bounds.w, el.x_mm + move.dx)),
                      y_mm: Math.max(0, Math.min(working.height_mm - bounds.h, el.y_mm + move.dy)),
                    });
                  }}>{move.label}</button>
                ))}
              </div>
              <p style={{ fontSize: 12 }}>Ogni tocco sposta il campo di 0,5 mm. Per trascinarlo, attiva il trascinamento nell’anteprima.</p>
              {el.type === 'expiry_weekday' && <p>Giorno calcolato dalla scadenza: M/ar per martedì, M/er per mercoledì. Posiziona il riquadro senza coprire gli altri campi.</p>}
              {weekdayOverlaps && <p role="status" style={{ padding: 12, background: '#fff4ce', color: '#503600', borderRadius: 8 }}>Questo elemento potrebbe coprire altri campi. Spostalo con le frecce o riducine la dimensione e controlla l’anteprima prima di salvare.</p>}
              {el.type === 'text' && (
                <>
                  {/\bproduct\b/.test(el.binding ?? '') && <label style={{ display: 'block', marginBottom: 12 }}>
                    Nome da provare nell’anteprima (non modifica la ricetta)
                    <textarea aria-label="Nome di prova" value={String(editorPreviewData.product ?? '')}
                      onChange={event => setSampleProduct(event.target.value)} rows={3} maxLength={200}
                      style={{ width: '100%', padding: 10, border: '1px solid #94a3b8', borderRadius: 8, background: '#fff' }} />
                  </label>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, marginBottom: 12 }}>
                    <input type="checkbox" checked={el.text_fit === 'shrink' || (el.text_fit == null && /\bproduct\b/.test(el.binding ?? ''))}
                      onChange={event => patchElement(el.id, { text_fit: event.target.checked ? 'shrink' : 'wrap' })} />
                    {' '}Adatta testo alla casella
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <NumField
                      label="Larghezza (mm)"
                      value={el.width_mm ?? 0}
                      onChange={(v) => patchElement(el.id, { width_mm: v > 0 ? v : null })}
                      min={0} max={working.width_mm} step={1}
                      allowClear
                    />
                    <NumField
                      label="Altezza (mm)"
                      value={el.height_mm ?? 0}
                      onChange={(v) => patchElement(el.id, { height_mm: v > 0 ? v : null })}
                      min={0} max={working.height_mm} step={1}
                      allowClear
                    />
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
                    {el.width_mm
                      ? 'Il testo va a capo nella casella. Con adattamento attivo il carattere si riduce fino a 4 pt; aumenta l’altezza per ospitare più righe.'
                      : 'Con adattamento attivo usa lo spazio fino al prossimo campo. Imposta larghezza e altezza per riservare una casella.'}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-end' }}>
                    <NumField
                      label="Dim. font (pt)"
                      value={el.font_size_pt ?? 6}
                      onChange={(v) => patchElement(el.id, { font_size_pt: v })}
                      min={2} max={24} step={0.5}
                    />
                    <button
                      type="button"
                      onClick={() => patchElement(el.id, { font_weight: el.font_weight === 'bold' ? 'normal' : 'bold' })}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: `1px solid ${el.font_weight === 'bold' ? '#0066cc' : '#cbd5e1'}`,
                        background: el.font_weight === 'bold' ? '#0066cc' : '#fff',
                        color: el.font_weight === 'bold' ? '#fff' : '#475569',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      B  Bold
                    </button>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                      Cosa scrive questo testo?
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {DEFAULT_BINDINGS.filter((b) => !b.binding.includes('trace_url')).map((b) => {
                        const active = (el.binding ?? '') === b.binding;
                        return (
                          <button
                            key={b.binding}
                            type="button"
                            onClick={() => patchElement(el.id, { binding: b.binding })}
                            style={{
                              border: `2px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                              background: active ? '#0066cc' : '#fff',
                              color: active ? '#fff' : '#334155',
                              borderRadius: 10, padding: '12px 10px',
                              fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {b.label}
                          </button>
                        );
                      })}
                    </div>
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
                        Testo personalizzato / avanzato
                      </summary>
                      <IonInput
                        value={el.binding ?? ''}
                        onIonInput={(e) => patchElement(el.id, { binding: String(e.detail.value ?? '') })}
                        placeholder="Es. Lotto: {{ lp_code | strip:LP- }}"
                        style={{
                          marginTop: 8,
                          fontSize: 13, fontFamily: 'monospace',
                          '--padding-start': '10px', '--padding-end': '10px',
                          '--padding-top': '10px', '--padding-bottom': '10px',
                          '--background': '#eef2f7', '--border-radius': '10px',
                        } as React.CSSProperties}
                      />
                      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                        Scrivi testo fisso, oppure usa i segnaposto {'{{ campo }}'} con
                        filtri (es. <code>| upper</code>, <code>| date:DD/MM/YYYY</code>).
                      </div>
                    </details>
                  </div>
                </>
              )}

              {(el.type === 'qr' || el.type === 'barcode' || el.type === 'expiry_weekday') && (
                <>
                  {el.type === 'qr' && <label style={{ display: 'block', marginBottom: 12 }}>
                    Formato del codice
                    <select aria-label="Formato del codice" value={el.qr_format ?? 'qr'}
                      onChange={event => {
                        const rectangular = event.target.value === 'rmqr';
                        let width = 18;
                        let height = 18;
                        if (rectangular) {
                          try {
                            const code = rectangularQr(resolveBinding(el.binding ?? '{{ trace_url }}', editorPreviewData));
                            const dpi = props.rasterOptions?.dpi ?? 203;
                            width = Math.ceil(code.width * 3 * 25.4 / dpi * 2) / 2;
                            height = Math.ceil(code.height * 3 * 25.4 / dpi * 2) / 2;
                          } catch { width = 31; height = 9; }
                        }
                        patchElement(el.id, { qr_format: rectangular ? 'rmqr' : 'qr',
                          width_mm: width, height_mm: height,
                          x_mm: Math.max(0, Math.min(el.x_mm, working.width_mm - width)),
                          y_mm: Math.max(0, Math.min(el.y_mm, working.height_mm - height)),
                        });
                      }} style={{ display: 'block', width: '100%', padding: 12, marginTop: 6 }}>
                      <option value="qr">QR standard (quadrato)</option>
                      <option value="rmqr">rMQR sperimentale (rettangolare)</option>
                    </select>
                    {el.qr_format !== 'rmqr' && <p>{recommendedQrWidth ? `Dimensione consigliata: ${recommendedQrWidth} mm, bordo bianco incluso. ` : ''}Puoi ridurlo e stampare; verifica la lettura sulla carta.</p>}
                    {el.qr_format === 'rmqr' && <p>Prova la scansione con i tuoi telefoni prima di usarlo sulle etichette. Il bordo bianco è incluso nelle dimensioni.</p>}
                  </label>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <NumField
                      label="Larghezza (mm)"
                      value={el.width_mm ?? 18}
                      onChange={(v) => patchElement(el.id, {
                        width_mm: v,
                        height_mm: (el.type === 'qr' && el.qr_format !== 'rmqr') || el.type === 'expiry_weekday' ? v : el.height_mm,
                      })}
                      min={5} max={el.qr_format === 'rmqr' ? working.width_mm : Math.min(working.width_mm, working.height_mm)} step={1}
                    />
                    {(el.type === 'barcode' || (el.type === 'qr' && el.qr_format === 'rmqr')) && (
                      <NumField
                        label="Altezza (mm)"
                        value={el.height_mm ?? 8}
                        onChange={(v) => patchElement(el.id, { height_mm: v })}
                        min={3} max={working.height_mm} step={1}
                      />
                    )}
                  </div>
                  {el.type !== 'expiry_weekday' && <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                      Dato codificato
                    </div>
                    <IonInput
                      value={el.binding ?? ''}
                      onIonInput={(e) => patchElement(el.id, { binding: String(e.detail.value ?? '') })}
                      style={{
                        fontSize: 13, fontFamily: 'monospace',
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '8px', '--padding-bottom': '8px',
                        '--background': '#eef2f7', '--border-radius': '10px',
                      } as React.CSSProperties}
                    />
                  </div>}
                </>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <IonButton
                  size="small" fill="outline"
                  disabled={selIdx === 0}
                  aria-label="Sposta elemento prima" onClick={() => moveElement(el.id, -1)}
                  style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}
                >
                  <IonIcon icon={arrowUpOutline} slot="icon-only" />
                </IonButton>
                <IonButton
                  size="small" fill="outline"
                  disabled={selIdx === working.elements.length - 1}
                  aria-label="Sposta elemento dopo" onClick={() => moveElement(el.id, 1)}
                  style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}
                >
                  <IonIcon icon={arrowDownOutline} slot="icon-only" />
                </IonButton>
                <IonButton
                  size="small" fill="outline" color="danger"
                  aria-label="Elimina elemento" onClick={() => { deleteElement(el.id); setExpandedId(null); }}
                  style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}
                >
                  <IonIcon icon={trashOutline} slot="icon-only" />
                </IonButton>
              </div>
            </div>
          );
        })()}
        </div>
        </div>
        </div>
      </IonContent>
    </IonModal>
  );
}

// ───────── Sotto-componente NumField ─────────
interface NumFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /**
   * Se true, svuotare il campo committa 0 (il chiamante lo mappa su "nessuna
   * casella" / null) e il display resta vuoto. Se false (default), svuotare
   * lascia il campo vuoto durante la digitazione ma NON tocca il modello, e al
   * blur ripristina il valore reale: niente "0" residuo, niente valori invalidi.
   */
  allowClear?: boolean;
}

function NumField(props: NumFieldProps): JSX.Element {
  const { value, onChange, allowClear } = props;
  // Stato di testo locale: un input controllato direttamente sul number lasciava
  // uno "0" quando si cancellava (non si poteva svuotare la casella).
  const fmt = useCallback(
    (n: number): string => (allowClear && n === 0 ? '' : String(n)),
    [allowClear],
  );
  const [text, setText] = useState<string>(() => fmt(value));
  const lastPropagated = useRef<number>(value);

  useEffect(() => {
    // Sincronizza il display solo su cambi del modello che NON arrivano dal
    // nostro stesso onChange (es. selezione di un altro elemento).
    if (value !== lastPropagated.current) {
      setText(fmt(value));
      lastPropagated.current = value;
    }
  }, [value, fmt]);

  return (
    <div style={{ flex: 1 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: '#475569',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.02em',
      }}>
        {props.label}
      </div>
      <input
        aria-label={props.label}
        type="number"
        inputMode="decimal"
        value={text}
        min={props.min}
        max={props.max}
        step={props.step ?? 0.5}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          setText(raw);
          if (raw.trim() === '') {
            if (allowClear) { lastPropagated.current = 0; onChange(0); }
            return; // vuoto: campo vuoto in display, modello invariato (o 0 se allowClear)
          }
          const v = Number(raw);
          if (Number.isFinite(v) && (props.min == null || v >= props.min) && (props.max == null || v <= props.max)) { lastPropagated.current = v; onChange(v); }
        }}
        onBlur={() => {
          const empty = text.trim() === '';
          if ((empty && !allowClear) || (!empty && (!Number.isFinite(Number(text)) || (props.min != null && Number(text) < props.min) || (props.max != null && Number(text) > props.max)))) {
            setText(fmt(value)); // ripristina il valore reale (no 0 spurio, no invalido)
          }
        }}
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 10,
          border: '1px solid #cbd5e1',
          background: '#eef2f7',
          fontSize: 14,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'center',
          minHeight: 44, boxSizing: 'border-box',
        }}
      />
    </div>
  );
}
